import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/modules/auth'
import { SubscriptionsApi, SubscriptionStatus, LimitCheckResult, StorageStatus } from '@/shared/api/subscriptions'

const DEFAULT_FREE_TIER: SubscriptionStatus = {
  tier: 'free',
  limits: {
    tripLimit: 2,
    crewLimit: 8,
    vehicleLimit: -1,
    photoLimit: -1
  },
  features: {
    canCreateTrip: false,
    canAddCrewMember: false,
    canAddVehicle: true,
    canUploadPhoto: false,
    canUseSnapToRoad: false,
    canExportGpx: false,
    canUseDispersedCampingFinder: false,
    canUseRanger: false,
    canUse3dRoutePreview: false,
    canUseFullSharingPermissions: false
  }
}

export function useSubscription() {
  const { getAccessToken, isAuthenticated } = useAuth()

  const api = useMemo(() => new SubscriptionsApi(getAccessToken), [getAccessToken])

  // Use React Query for subscription status with global caching
  const { data: status, isLoading: loading, error, refetch } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.getStatus(),
    staleTime: 30 * 60 * 1000, // 30 minutes - subscriptions rarely change
    gcTime: 60 * 60 * 1000, // 1 hour
    enabled: isAuthenticated,
    retry: 1,
    placeholderData: DEFAULT_FREE_TIER, // Show default while loading
  })

  const { data: storageStatus, isLoading: storageLoading } = useQuery<StorageStatus>({
    queryKey: ['subscription', 'storage'],
    queryFn: () => api.getStorageStatus(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: isAuthenticated,
    retry: 1,
  })

  const checkTripLimit = useCallback(async (): Promise<LimitCheckResult> => {
    try {
      return await api.getTripLimit()
    } catch (err) {
      return {
        allowed: false,
        current: 0,
        limit: 3,
        isUnlimited: false
      }
    }
  }, [api])

  const checkCrewLimit = useCallback(async (tripId: string): Promise<LimitCheckResult> => {
    try {
      return await api.getCrewLimit(tripId)
    } catch (err) {
      return {
        allowed: false,
        current: 0,
        limit: 5,
        isUnlimited: false
      }
    }
  }, [api])

  const checkVehicleLimit = useCallback(async (): Promise<LimitCheckResult> => {
    try {
      return await api.getVehicleLimit()
    } catch (err) {
      return {
        allowed: false,
        current: 0,
        limit: 3,
        isUnlimited: false
      }
    }
  }, [api])

  return {
    status: status || DEFAULT_FREE_TIER,
    loading,
    error: error as Error | null,
    refetch,
    checkTripLimit,
    checkCrewLimit,
    checkVehicleLimit,
    isFree: status?.tier === 'free',
    isPro: status?.tier === 'pro',
    canUseRanger: status?.features.canUseRanger ?? false,
    storageStatus: storageStatus ?? null,
    storageLoading,
  }
}

